import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { useSelectedDealer, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { PASSWORD_STRENGTH_THRESHOLD } from "modules/Login/components";
import { UserForm, useCreateUserMutation } from "modules/UsersDashboard/components";
import "modules/UsersDashboard/components/CreateUserModal/CreateUserModal.scss";
import { UserFormHeaderCheckboxes } from "modules/UsersDashboard/components/UserForm/UserFormHeaderCheckboxes";

import type { CreateEditUserForm } from "modules/UsersDashboard/components";

type CreateUserModalProps = {
  onClose: () => void;
};

const CREATE_USER_FORM_ID = "create-user-form";
const FormHelper = createFormHelper<CreateEditUserForm>();

export const CreateUserModal = ({ onClose }: CreateUserModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const { mutateAsync: createUser, isPending } = useCreateUserMutation();

  const formMethods = FormHelper.useForm({
    defaultValues: {
      dealers_ids: null,
      location_ids: null,
      brand_group_ids: [],
      confirmPassword: "",
      dms_nr: null,
      dms_location_source: "",
      external_user_id: "",
      email: "",
      first_name: "",
      is_combi_user: false,
      is_counter_tablet_user: false,
      csv_download: false,
      is_keylocker_allowed: false,
      multi_device_login_enabled: false,
      tyre_ordering_enabled: false,
      last_name: "",
      dealer_location_id: selectedLocation?.id ?? null,
      password: "",
      phone: "",
      role_id: null,
      username: "",
      isActive: true,
      onei_planning_monteur_id: null,
      planit_planning_mechanic_id: null,
      dealer_id: selectedDealer?.id ?? null,
      has_leased_tablet: false
    },
    mode: "all"
  });
  const { isSubmitting } = formMethods.formState;

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const handleSetPasswordStrength = (strength: number) => setPasswordStrength(strength);

  const validateUser = (values: CreateEditUserForm) => {
    let hasErrors =
      !values.first_name ||
      !values.last_name ||
      !values.email ||
      !values.username ||
      !values.role_id ||
      !values.password ||
      !values.confirmPassword ||
      !values.dealer_location_id ||
      !values.dealer_id;

    if (values.password && values.password !== values.confirmPassword) hasErrors = true;
    if (values.password && passwordStrength < PASSWORD_STRENGTH_THRESHOLD) hasErrors = true;

    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: CreateEditUserForm) => {
    if (validateUser(values)) return;

    await createUser(values);
    handleClose();
  };

  return (
    <Modal open size="large" className="CreateUserModal">
      <FormHelper.FormProvider {...formMethods}>
        <Modal.Header>
          <div className="header-container">
            <div>{t("v8_add_new_user", "Add New User")}</div>
            <UserFormHeaderCheckboxes disabled={false} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <Form id={CREATE_USER_FORM_ID} onSubmit={formMethods.handleSubmit(handleSubmit)} loading={isSubmitting} disabled={isSubmitting}>
            <UserForm
              hasErrors={hasErrors}
              canEditAll
              canEditDealerAdminFields={false}
              passwordStrength={passwordStrength}
              onPasswordStrengthChange={handleSetPasswordStrength}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <div className="action-buttons-container">
            <Button className="light" type="button" onClick={handleClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button type="submit" form={CREATE_USER_FORM_ID} color="green" disabled={isPending} loading={isPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </Modal.Actions>
      </FormHelper.FormProvider>
    </Modal>
  );
};
